import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Lock, AlertTriangle } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockSecs, setLockSecs] = useState(0);

  useEffect(() => {
    if (sessionStorage.getItem("mp_session_expired")) {
      sessionStorage.removeItem("mp_session_expired");
      toast.error("Session expired, please login again.");
    }
  }, []);

  useEffect(() => {
    if (lockSecs <= 0) return;
    const id = setInterval(() => setLockSecs((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [lockSecs]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password, "admin");
      if (u.role !== "admin" && u.role !== "subadmin") {
        toast.error("Not an admin account");
        return;
      }
      toast.success("Admin login successful");
      navigate("/admin");
    } catch (err) {
      const secs = parseInt(err.response?.headers?.["retry-after"] || "0", 10);
      if (err.response?.status === 429 && secs > 0) setLockSecs(secs);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-12 bg-brand-ink relative overflow-hidden" data-testid="staff-login-page">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(45deg,#fff 0 1px,transparent 1px 14px)" }} />
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-terracotta/20 blur-3xl" />
      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center justify-center gap-2 mb-5">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.2em] uppercase text-brand-terracotta bg-brand-terracotta/10 border border-brand-terracotta/30 rounded-full px-3 py-1" data-testid="staff-portal-badge">
            <Lock className="w-3 h-3" /> Staff Portal
          </span>
        </div>
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="w-14 h-14 rounded-2xl bg-brand-terracotta/15 border border-brand-terracotta/30 flex items-center justify-center mb-5">
            <ShieldCheck className="w-7 h-7 text-brand-terracotta" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-white">Admin &amp; Sub-Admin Login</h1>
          <p className="text-white/50 text-sm mt-1.5 mb-5">Restricted control panel for My Property staff. Customers &amp; mediators should use the <span className="text-white/80">main login</span> page.</p>
          <div className="flex items-start gap-2 rounded-xl bg-brand-terracotta/10 border border-brand-terracotta/25 text-white/80 text-xs px-3.5 py-2.5 mb-6" data-testid="staff-warning">
            <AlertTriangle className="w-4 h-4 text-brand-terracotta shrink-0 mt-0.5" />
            <span>Authorized personnel only. All login activity is monitored and logged.</span>
          </div>
          <form onSubmit={submit} className="space-y-4">
            {lockSecs > 0 && (
              <div className="rounded-xl bg-white/10 border border-white/20 text-white text-sm px-4 py-3" data-testid="admin-lockout-banner">
                Locked after too many attempts. Try again in <span className="font-semibold tabular-nums">{Math.floor(lockSecs / 60)}:{String(lockSecs % 60).padStart(2, "0")}</span>
              </div>
            )}
            <div>
              <Label className="text-white/80">Email or Username</Label>
              <Input type="text" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email" placeholder="admin email or sub-admin username" className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40" />
            </div>
            <div>
              <Label className="text-white/80">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password" className="mt-1.5 bg-white/10 border-white/20 text-white" />
            </div>
            <Button type="submit" disabled={loading || lockSecs > 0} data-testid="admin-submit" className="w-full rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover">
              {lockSecs > 0 ? `Locked (${Math.floor(lockSecs / 60)}:${String(lockSecs % 60).padStart(2, "0")})` : loading ? "..." : "Enter Dashboard"}
            </Button>
            <div className="text-center pt-1">
              <Link to="/forgot-password" data-testid="admin-forgot-password-link" className="text-xs text-white/60 hover:text-white hover:underline">Forgot password? Reset via email</Link>
            </div>
          </form>
        </div>
        <p className="text-center text-white/30 text-xs mt-5">My Property · Secure Staff Access</p>
      </div>
    </div>
  );
}
